import React, { useEffect, useRef } from 'react';
import { ABNTSection } from '@/data/manuals/fleetone-abnt-content';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { safeHighlightSearch } from '@/lib/security-utils';

interface ManualABNTContentProps {
  sections: ABNTSection[];
  onScrollSpy: (sectionNumber: string) => void;
  searchQuery?: string;
  containerRef: React.RefObject<HTMLDivElement>;
}

export const ManualABNTContent: React.FC<ManualABNTContentProps> = ({
  sections,
  onScrollSpy,
  searchQuery = '',
  containerRef
}) => {
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Scroll spy - detect which section is currently visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionNumber = entry.target.getAttribute('data-section');
            if (sectionNumber) {
              onScrollSpy(sectionNumber);
            }
          }
        });
      },
      {
        root: containerRef.current,
        rootMargin: '-80px 0px -80% 0px',
        threshold: 0
      }
    );

    sectionRefs.current.forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, [sections, onScrollSpy, containerRef]);

  const parseMarkdown = (content: string): string => {
    // Configure marked to allow HTML within markdown
    marked.setOptions({
      breaks: true,
      gfm: true
    });
    
    const html = marked(content) as string;
    
    // Add inline styles to lists as fallback
    const withListStyles = html
      .replace(/<ul>/g, '<ul style="list-style-type: disc; margin: 8px 0 14px 20px; padding-left: 20px;">')
      .replace(/<ol>/g, '<ol style="list-style-type: decimal; margin: 8px 0 14px 20px; padding-left: 20px;">')
      .replace(/<li>/g, '<li style="display: list-item; margin: 6px 0;">');
    
    // Sanitize allowing HTML with classes
    return DOMPurify.sanitize(withListStyles, {
      ALLOWED_TAGS: ['p', 'ul', 'ol', 'li', 'strong', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span', 'code', 'pre', 'blockquote', 'a'],
      ALLOWED_ATTR: ['class', 'style', 'href', 'target', 'rel'],
      ALLOW_DATA_ATTR: false
    });
  };

  const highlightSearch = (text: string): string => {
    return safeHighlightSearch(text, searchQuery);
  };

  const renderSection = (section: ABNTSection, isTopLevel: boolean = false) => {
    const TitleTag = section.level === 1 ? 'h2' : section.level === 2 ? 'h3' : 'h4';

    return (
      <section
        key={section.number}
        id={`section-${section.number}`}
        data-section={section.number}
        ref={(el) => {
          if (el) sectionRefs.current.set(section.number, el);
        }}
        className={isTopLevel ? 'manual-section' : ''}
      >
        <TitleTag>
          {section.number}. {section.title}
        </TitleTag>

        <div 
          dangerouslySetInnerHTML={{
            __html: highlightSearch(parseMarkdown(section.content))
          }}
        />

        {section.subsections?.map((subsection) => renderSection(subsection, false))}
      </section>
    );
  };

  return (
    <div className="space-y-4">
      {sections.map((section) => renderSection(section, true))}
    </div>
  );
};
